/**
 * AVANCE y ASISTENCIA vistos por el ALUMNO (D1 · E3) — selectores PUROS.
 *
 * Todo lo que necesitan sale de lo que `my_cohorts_student` ya replica a su
 * dispositivo: `attendance_record` filtrado por `user_id`, `class_session` de
 * sus cohortes y `session_module` (el plan de clase). Aquí no se recalcula nada
 * que ya calcule el servidor:
 *
 *  · El **porcentaje** de asistencia lo deriva el servidor
 *    (`use_cases::attendance_stats::attendance_rate`) y lo denormaliza en
 *    `cohort_enrollment`; se lee con `attendanceStatsOf` y NO se vuelve a
 *    contar aquí.
 *  · El **objetivo** de asistencia sale del criterio de certificación
 *    (`academy.completion.attendance_pct`) que evalúa el servidor y sirve por
 *    `/api/cohorts/:id/my-completion`; aquí solo se extrae del desglose.
 *  · El progreso del plan de clase reutiliza `planProgress` (session-plan).
 *
 * Lo que sí es de esta capa: unir MIS asistencias con la sesión a la que
 * pertenecen (el histórico), enfrentar el porcentaje al objetivo, y componer la
 * rejilla de clases del curso.
 */

import type { AttendanceStats } from "../people/selectors";
import { type PlanCompletionCtx, type PlanModule, planProgress } from "../session-plan/types";

/** Los cuatro estados de una `attendance_record`. */
export type AttendanceStatusValue = "present" | "late" | "absent" | "excused";

const ATTENDANCE_STATUSES: readonly AttendanceStatusValue[] = [
  "present",
  "late",
  "absent",
  "excused",
];

/** Coacciona el estado crudo de una fila a uno conocido (`null` si no lo es). */
export function parseAttendanceStatus(
  status: string | null | undefined,
): AttendanceStatusValue | null {
  return ATTENDANCE_STATUSES.find((s) => s === status) ?? null;
}

/** Fila mínima de `attendance_record` (lo que llega por sync). */
export interface AttendanceRecordRow {
  id: string;
  class_session_id?: string | null;
  cohort_id?: string | null;
  status?: string | null;
}

/** Fila mínima de `class_session` (lo que llega por sync, sin server-only). */
export interface ClassSessionRow {
  id: string;
  cohort_id?: string | null;
  date?: string | null;
  start_time?: string | null;
  topic?: string | null;
  deleted_at?: string | null;
}

/** Una clase del histórico de asistencia del alumno. */
export interface AttendanceTimelineRow {
  sessionId: string;
  date: string | null;
  startTime: string | null;
  topic: string | null;
  status: AttendanceStatusValue;
}

/**
 * MI histórico de asistencia de una cohorte, de la clase más reciente a la más
 * antigua. Solo aparecen las sesiones VIVAS con una asistencia registrada: sin
 * registro no hubo pase de lista, y pintar una fila vacía induciría a leerla
 * como falta.
 */
export function attendanceTimeline(
  records: readonly AttendanceRecordRow[],
  sessions: readonly ClassSessionRow[],
): AttendanceTimelineRow[] {
  const sessionById = new Map<string, ClassSessionRow>();
  for (const s of sessions) {
    if (s.deleted_at == null) {
      sessionById.set(s.id, s);
    }
  }
  const rows: AttendanceTimelineRow[] = [];
  for (const r of records) {
    const session = r.class_session_id ? sessionById.get(r.class_session_id) : undefined;
    const status = parseAttendanceStatus(r.status);
    if (!session || !status) {
      continue;
    }
    rows.push({
      sessionId: session.id,
      date: session.date ?? null,
      startTime: session.start_time ?? null,
      topic: session.topic ?? null,
      status,
    });
  }
  return rows.toSorted(
    (a, b) =>
      (b.date ?? "").localeCompare(a.date ?? "") ||
      (b.startTime ?? "").localeCompare(a.startTime ?? "") ||
      b.sessionId.localeCompare(a.sessionId),
  );
}

/** Desglose de un criterio de certificación (forma de `CriterionBreakdown`). */
export interface CriterionProgressRow {
  key: string;
  met: boolean;
  current: number;
  target: number;
}

/** Mi asistencia frente al criterio de certificación de la cohorte. */
export interface AttendanceCertification {
  /** Porcentaje 0..100 alcanzado, o `null` si aún no hay registro. */
  ratePct: number | null;
  /** Mínimo exigido 0..100, o `null` si la cohorte no exige asistencia. */
  targetPct: number | null;
  /** `true`/`false` frente al objetivo; `null` sin objetivo o sin dato. */
  met: boolean | null;
  /** ¿Hay algo que enseñar (porcentaje u objetivo)? */
  hasData: boolean;
}

/**
 * Enfrenta MI porcentaje de asistencia al mínimo del criterio de certificación.
 *
 * El porcentaje preferido es el del criterio evaluado por el SERVIDOR (misma
 * cuenta, ya redondeada); las stats denormalizadas de la matrícula son el
 * respaldo cuando la cohorte no tiene reglas configuradas y por tanto no hay
 * criterio que consultar.
 */
export function attendanceCertification(
  stats: AttendanceStats,
  criteria: readonly CriterionProgressRow[] = [],
): AttendanceCertification {
  const criterion = criteria.find((c) => c.key === "attendance_pct");
  const ratePct = criterion ? criterion.current : stats.rate;
  const targetPct = criterion ? criterion.target : null;
  return {
    ratePct,
    targetPct,
    met: criterion ? criterion.met : null,
    hasData: ratePct !== null || targetPct !== null,
  };
}

/** Una clase en la rejilla de avance del alumno. */
export interface CohortSessionProgressRow {
  sessionId: string;
  date: string | null;
  startTime: string | null;
  topic: string | null;
  /** Estado de asistencia registrado, o `null` si no hubo pase de lista. */
  attendance: AttendanceStatusValue | null;
  /** Ítems del plan hechos / que puntúan (0/0 si la clase no tiene plan). */
  planDone: number;
  planTotal: number;
}

/** Resumen agregado de la rejilla (la barra global del curso). */
export interface CohortProgressSummary {
  sessions: CohortSessionProgressRow[];
  /** Clases con asistencia `present` o `late` (el criterio del servidor). */
  attended: number;
  /** Clases con pase de lista registrado. */
  recorded: number;
  planDone: number;
  planTotal: number;
}

/**
 * Rejilla «mis N clases» de una cohorte: por cada clase viva, si asistí y
 * cuánto del plan llevo hecho. El progreso por clase lo cuenta `planProgress`
 * (session-plan) — aquí no hay una segunda definición de «hecho».
 */
export function cohortSessionsProgress(
  sessions: readonly ClassSessionRow[],
  planBySession: ReadonlyMap<string, readonly PlanModule[]>,
  planCtx: PlanCompletionCtx,
  records: readonly AttendanceRecordRow[],
): CohortProgressSummary {
  const statusBySession = new Map<string, AttendanceStatusValue>();
  for (const r of records) {
    const status = parseAttendanceStatus(r.status);
    if (r.class_session_id && status) {
      statusBySession.set(r.class_session_id, status);
    }
  }

  const rows = sessions
    .filter((s) => s.deleted_at == null)
    .toSorted(
      (a, b) =>
        (a.date ?? "").localeCompare(b.date ?? "") ||
        (a.start_time ?? "").localeCompare(b.start_time ?? "") ||
        a.id.localeCompare(b.id),
    )
    .map((s): CohortSessionProgressRow => {
      const plan = planProgress(planBySessionOrEmpty(planBySession, s.id), planCtx);
      return {
        sessionId: s.id,
        date: s.date ?? null,
        startTime: s.start_time ?? null,
        topic: s.topic ?? null,
        attendance: statusBySession.get(s.id) ?? null,
        planDone: plan.done,
        planTotal: plan.total,
      };
    });

  return {
    sessions: rows,
    attended: rows.filter((r) => r.attendance === "present" || r.attendance === "late").length,
    recorded: rows.filter((r) => r.attendance !== null).length,
    planDone: rows.reduce((acc, r) => acc + r.planDone, 0),
    planTotal: rows.reduce((acc, r) => acc + r.planTotal, 0),
  };
}

const EMPTY_PLAN: readonly PlanModule[] = [];

function planBySessionOrEmpty(
  planBySession: ReadonlyMap<string, readonly PlanModule[]>,
  sessionId: string,
): readonly PlanModule[] {
  return planBySession.get(sessionId) ?? EMPTY_PLAN;
}
